import React, { memo, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { SwaggerFile } from '../../types/swaggerFile';
import { monoSx, pillButtonSx } from '../dashboard/styles';

interface ConfirmDeleteFileDialogProps {
  /** File awaiting confirmation; null closes the dialog. */
  file: SwaggerFile | null;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDeleteFileDialog: React.FC<ConfirmDeleteFileDialogProps> = ({ file, deleting, onCancel, onConfirm }) => {
  // Keep the name rendered while the dialog fades out.
  const lastName = useRef('');
  if (file) lastName.current = file.originalName;

  return (
    <Dialog
      open={Boolean(file)}
      onClose={deleting ? undefined : onCancel}
      role="alertdialog"
      aria-labelledby="confirm-delete-file-title"
      PaperProps={{ sx: { width: '100%', maxWidth: 420, borderRadius: 5, p: 3, display: 'flex', flexDirection: 'column', gap: 2 } }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'error.main',
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
        }}
      >
        <DeleteOutline />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography id="confirm-delete-file-title" component="h2" sx={{ fontSize: 20, fontWeight: 600 }}>
          Delete this file?
        </Typography>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5, wordBreak: 'break-word' }}>
          <Box component="span" sx={{ ...monoSx, fontSize: 13, color: 'text.primary' }}>
            {lastName.current}
          </Box>{' '}
          will no longer be available for mapping documents. This can’t be undone.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Button onClick={onCancel} disabled={deleting} sx={pillButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutline />}
          // The theme forces contained buttons to the brand color; keep destructive red.
          sx={{ ...pillButtonSx, bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
        >
          Delete file
        </Button>
      </Box>
    </Dialog>
  );
};

export default memo(ConfirmDeleteFileDialog);
